import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { BodyResult } from "../kernel/protocol.js";
import { ecartements } from "./eclatement.js";

/**
 * Le viewport. Volontairement en three.js nu plutot qu'en react-three-fiber :
 * la selection d'arete et de face passera par du lancer de rayon sur des
 * attributs personnalises, et une couche de reconciliation React entre les deux
 * gene plus qu'elle n'aide.
 *
 * L'unite de la scene est le millimetre, comme le document.
 */

/** Un corps a l'ecran : sa geometrie, ses aretes, et vers ou il s'ecarte. */
interface CorpsAffiche {
  groupe: THREE.Group;
  /** Du centre de l'assemblage vers le centre de ce corps. */
  ecart: THREE.Vector3;
}

const BACKGROUND = 0x1e1f22;
const SURFACE = 0xc9cdd4;
const EDGE = 0x2b2d31;

export class Scene {
  readonly #renderer: THREE.WebGLRenderer;
  readonly #scene = new THREE.Scene();
  readonly #camera: THREE.PerspectiveCamera;
  readonly #controls: OrbitControls;
  readonly #bodies = new THREE.Group();
  readonly #corps: CorpsAffiche[] = [];
  /** Facteur d'eclatement courant, garde d'une evaluation a l'autre. */
  #eclatement = 0;
  readonly #resizeObserver: ResizeObserver;
  #disposed = false;

  constructor(canvas: HTMLCanvasElement, container: HTMLElement) {
    this.#renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.#renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.#scene.background = new THREE.Color(BACKGROUND);

    this.#camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10_000);
    this.#camera.position.set(120, -120, 90);
    this.#camera.up.set(0, 0, 1); // Z vers le haut, comme toute la CAO.

    this.#controls = new OrbitControls(this.#camera, this.#renderer.domElement);
    this.#controls.enableDamping = true;

    const grid = new THREE.GridHelper(200, 20, 0x3a3d44, 0x2a2c31);
    grid.rotation.x = Math.PI / 2; // GridHelper est dans le plan XZ, on le couche en XY.
    this.#scene.add(grid);
    this.#scene.add(new THREE.AxesHelper(30));

    this.#scene.add(new THREE.AmbientLight(0xffffff, 1.4));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(100, -150, 200);
    this.#scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.8);
    fill.position.set(-120, 100, -60);
    this.#scene.add(fill);

    this.#scene.add(this.#bodies);

    this.#resizeObserver = new ResizeObserver(() => this.#resize(container));
    this.#resizeObserver.observe(container);
    this.#resize(container);
    this.#renderer.setAnimationLoop(() => this.#tick());
  }

  /**
   * Remplace les corps affiches. Les anciens sont liberes explicitement.
   *
   * Chaque corps est un groupe qui porte son maillage ET ses aretes : sans
   * cela, ecarter l'un laisserait l'autre en place, et la piece se dedoublerait
   * en fantomes de contours.
   */
  setBodies(bodies: readonly BodyResult[]): void {
    this.#clearBodies();

    // Le calcul des ecarts vit dans `eclatement.ts`, verifiable hors navigateur.
    const ecarts = ecartements(bodies.map((body) => body.boundingBox));

    for (const [rang, body] of bodies.entries()) {
      const groupe = new THREE.Group();
      groupe.name = body.name;
      groupe.userData["featureId"] = body.featureId;
      // Une feature peut rendre plusieurs corps : `featureId` ne les distingue
      // plus, `bodyId` si. C'est lui qu'une selection devra designer.
      groupe.userData["bodyId"] = body.bodyId;

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(body.mesh.vertices, 3));
      geometry.setAttribute("normal", new THREE.BufferAttribute(body.mesh.normals, 3));
      geometry.setIndex(new THREE.BufferAttribute(body.mesh.indices, 1));

      const material = new THREE.MeshStandardMaterial({
        color: SURFACE,
        metalness: 0.1,
        roughness: 0.65,
        flatShading: false,
      });
      groupe.add(new THREE.Mesh(geometry, material));

      if (body.edges.length > 0) {
        const edgeGeometry = new THREE.BufferGeometry();
        edgeGeometry.setAttribute("position", new THREE.BufferAttribute(body.edges, 3));
        groupe.add(
          new THREE.LineSegments(edgeGeometry, new THREE.LineBasicMaterial({ color: EDGE })),
        );
      }

      this.#bodies.add(groupe);
      const ecart = ecarts[rang]!;
      this.#corps.push({ groupe, ecart: new THREE.Vector3(ecart[0], ecart[1], ecart[2]) });
    }

    // Un nouveau jeu de corps reprend l'eclatement en cours : sans cela, la
    // moindre frappe le remettrait a zero sous les doigts.
    this.eclater(this.#eclatement);
  }

  /**
   * Ecarte les corps de leur centre commun, pour voir ce qu'un debit a coupe.
   *
   * L'ecart est **proportionnel** a la distance au centre, et non d'une
   * longueur fixe : chaque morceau s'eloigne dans la direction ou il etait
   * deja, et l'arrangement de la piece reste lisible au lieu de se disperser
   * en etoile. Un morceau pile au centre ne bouge pas, ce qui est juste — il
   * est le pivot des autres.
   *
   * C'est une VUE, et rien d'autre. Le document ne bouge pas, l'export non
   * plus : ce qui descend en STL reste la piece a sa place.
   */
  eclater(facteur: number): void {
    this.#eclatement = facteur;
    for (const corps of this.#corps) {
      corps.groupe.position.copy(corps.ecart).multiplyScalar(facteur);
    }
  }

  /** Cadre la vue sur les corps affiches. */
  /**
   * Une vignette de ce qui est affiche, en PNG.
   *
   * Le rendu et la lecture doivent tenir dans le **meme bloc synchrone** : le
   * renderer n'a pas `preserveDrawingBuffer`, donc le tampon est perdu au
   * premier repaint, et un `toDataURL` differe rendrait une image vide.
   *
   * On repasse par un canvas 2D pour reduire : `toDataURL` sur le canvas WebGL
   * rendrait la taille du viewport, soit plusieurs centaines de kilo-octets
   * pour une image qui en fera vingt.
   */
  capturer(largeur = 240, hauteur = 180): string | undefined {
    const source = this.#renderer.domElement;
    if (source.width === 0 || source.height === 0) return undefined;

    // La piece entiere, quoi que l'utilisateur regarde. Capturer la vue
    // courante donnerait une vignette du coin ou il avait zoome : illisible
    // dans une liste, et differente a chaque enregistrement pour une piece qui
    // n'a pas change. On cadre, on capture, on remet la camera ou elle etait —
    // rien ne bouge a l'ecran, tout tient dans le meme bloc synchrone.
    //
    // L'eclatement tombe pour la meme raison : c'est un reglage de vue, et une
    // vignette qui en dependrait changerait sans que la piece ait change.
    const position = this.#camera.position.clone();
    const cible = this.#controls.target.clone();
    const eclatement = this.#eclatement;
    this.eclater(0);
    this.frameAll();
    this.#renderer.render(this.#scene, this.#camera);

    const remettre = (): void => {
      this.eclater(eclatement);
      this.#camera.position.copy(position);
      this.#controls.target.copy(cible);
      this.#controls.update();
    };

    const vignette = document.createElement("canvas");
    vignette.width = largeur;
    vignette.height = hauteur;
    const contexte = vignette.getContext("2d");
    if (contexte === null) {
      remettre();
      return undefined;
    }

    // Recadrage au centre plutot qu'ecrasement : une piece deformee dans sa
    // vignette ne se reconnait pas.
    const echelle = Math.max(largeur / source.width, hauteur / source.height);
    const l = source.width * echelle;
    const h = source.height * echelle;
    contexte.drawImage(source, (largeur - l) / 2, (hauteur - h) / 2, l, h);

    remettre();
    this.#renderer.render(this.#scene, this.#camera);

    return vignette.toDataURL("image/png");
  }

  frameAll(): void {
    const box = new THREE.Box3().setFromObject(this.#bodies);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) * 0.5 || 10;
    const distance = radius / Math.sin((this.#camera.fov * Math.PI) / 360);

    const direction = new THREE.Vector3(0.8, -0.9, 0.6).normalize();
    this.#camera.position.copy(center).addScaledVector(direction, distance * 1.6);
    this.#controls.target.copy(center);
    this.#controls.update();
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#resizeObserver.disconnect();
    this.#renderer.setAnimationLoop(null);
    this.#clearBodies();
    this.#controls.dispose();
    this.#renderer.dispose();
  }

  #clearBodies(): void {
    for (const child of [...this.#bodies.children]) {
      this.#bodies.remove(child);
      // `traverse` et non un test sur l'enfant : un corps est maintenant un
      // groupe, et s'arreter a lui laisserait maillage et aretes non liberes.
      child.traverse((objet) => {
        if (objet instanceof THREE.Mesh || objet instanceof THREE.LineSegments) {
          objet.geometry.dispose();
          const material = objet.material;
          if (Array.isArray(material)) material.forEach((m) => m.dispose());
          else material.dispose();
        }
      });
    }
    this.#corps.length = 0;
  }

  #resize(container: HTMLElement): void {
    const { clientWidth, clientHeight } = container;
    if (clientWidth === 0 || clientHeight === 0) return;
    this.#renderer.setSize(clientWidth, clientHeight, false);
    this.#camera.aspect = clientWidth / clientHeight;
    this.#camera.updateProjectionMatrix();
  }

  #tick(): void {
    this.#controls.update();
    this.#renderer.render(this.#scene, this.#camera);
  }
}
